import { CommonModule } from '@angular/common';
import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { SuperAdminCreateBatchPayload } from '@edtech-ui/core/models/admin-panel/batch-management/create-batch';
import { SuperAdminEditBatchPayload } from '@edtech-ui/core/models/admin-panel/batch-management/edit-batches';
import { DepartmentData } from '@edtech-ui/core/models/admin-panel/department-management/get-batch-department';
import { BatchManagementService } from '@edtech-ui/core/services/batch-management/batch-management.service';
import { DepartmentManagementService } from '@edtech-ui/core/services/department-management/department-management.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';

@Component({
	selector: 'app-create-batch-dialog',
	standalone: true,
	imports: [
		CommonModule, FormsModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatButtonModule, MatIcon, ReactiveFormsModule
	],
	templateUrl: './create-batch-dialog.component.html',
	styleUrls: ['./create-batch-dialog.component.scss']
})
export class CreateBatchDialogComponent implements OnInit, OnDestroy {
	departments: DepartmentData[] = [];
	years: number[] = [];
	filteredDepartments: DepartmentData[] = [];
	createBatchForm: FormGroup;
	subscription = new Subscription();
	@ViewChild('otherDeptInput') otherDeptInput!: ElementRef;
	isLoading = false;
	showOtherDepartmentField = false;

	constructor(private dialogRef: MatDialogRef<CreateBatchDialogComponent>,
		private _batchService: BatchManagementService,
		private _toastr: ToastrService,
		private _departmentService: DepartmentManagementService,
		@Inject(MAT_DIALOG_DATA) public data: any | null

	) {
		const currentYear = new Date().getFullYear();
		for (let i = currentYear - 10; i <= currentYear + 10; i++) {
			this.years.push(i);
		}

		this.createBatchForm = new FormGroup({
			batch_name: new FormControl('', [Validators.required]),
			academic_year_from: new FormControl('', [Validators.required]),
			academic_year_to: new FormControl('', [Validators.required]),
			department_id: new FormControl('', [Validators.required]),
			searchText: new FormControl(''),
			department_name: new FormControl('')
		})
	}
	ngOnInit(): void {
		this.getBatchDepartment();

		if (this.data) {
			this.createBatchForm.patchValue({
				batch_name: this.data.name,
				academic_year_from: this.data.from_year,
				academic_year_to: this.data.to_year,
				department_id: this.data.department?.id,
				department_name: this.data?.department_name
			});
		}
	}

	close() {
		this.dialogRef.close();
	}

	filterDepartments() {
		const search = this.createBatchForm.get('searchText')?.value?.toLowerCase() || '';
		this.filteredDepartments = this.departments.filter(d =>
			d.name.toLowerCase().includes(search)
		);
	}

	onOpen(opened: boolean) {
		if (!opened) {
			this.createBatchForm.get('searchText')?.setValue('');
			this.filteredDepartments = [...this.departments];
		}
	}

	postCreateBatch() {
		if (this.createBatchForm.invalid) {
			this.createBatchForm.markAllAsTouched();
			this._toastr.warning('Please fill all required fields before submitting.');
			return;
		}
				this.isLoading = true;

		const payload: SuperAdminCreateBatchPayload = {
			...this.createBatchForm.value,
		}

		if (this.data) {

			const payload: SuperAdminEditBatchPayload = {
				id: this.data.id,
				batch_name: this.createBatchForm.value.batch_name,
				academic_year_from: this.createBatchForm.value.academic_year_from,
				academic_year_to: this.createBatchForm.value.academic_year_to,
				department_id: String(this.createBatchForm.value.department_id),
			};

			this.subscription.add(
				this._batchService.putUpdateBatch(this.data.id, payload).pipe(
					tap(response => {
						if (response.Success) {
							this._toastr.success(response.Message);
							this.dialogRef.close(true);
						} else {
							this._toastr.error(response.Message);
						}
					}),
					finalize(() => {
						this.isLoading = false;
					}),
					catchError(() => {
						this._toastr.error('Something went wrong while creating while updating batches');
						return of(null);
					})
				).subscribe());
		} else {
			this.subscription.add(
				this._batchService.postBatch(payload).pipe(
					tap(response => {
						if (response.Success) {
							this._toastr.success(response.Message);
							this.dialogRef.close(true);
						} else {
							this._toastr.error(response.Message);
						}
					}), finalize(() => {
						this.isLoading = false;
					}),
					catchError(() => {
						this._toastr.error('Something went wrong while creating batches');
						return of(null);
					})
				).subscribe()
			);
		}
	}

	getBatchDepartment() {
		this.subscription.add(
			this._departmentService.getDepartmentForBatch().pipe(
				tap(response => {
					if (response.Success) {
						this.departments = response.Data;
						this.filteredDepartments = [...this.departments];
					} else {
						this._toastr.error(response.Message)
					}
				})
			).subscribe());
	}


	onDepartmentChange(selectedValue: string) {
		if (selectedValue === 'other') {
			this.showOtherDepartmentField = true;
			this.createBatchForm.get('department_name')?.setValidators([Validators.required]);
			setTimeout(() => this.otherDeptInput?.nativeElement.focus(), 0);
		} else {
			this.showOtherDepartmentField = false;
			this.createBatchForm.get('department_name')?.clearValidators();
			this.createBatchForm.get('department_name')?.reset();
		}
		this.createBatchForm.get('department_name')?.updateValueAndValidity();
	}


	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}
}